import React from 'react';
import { theme } from '../../styles/theme';
import { useWindowSize } from '../../hooks/useWindowSize';

interface AlbumNavigationProps {
  canNavigateLeft: boolean;
  canNavigateRight: boolean;
  onNavigateLeft: () => void;
  onNavigateRight: () => void;
  currentIndex: number;
  totalAlbums: number;
}

const AlbumNavigation: React.FC<AlbumNavigationProps> = ({
  canNavigateLeft,
  canNavigateRight,
  onNavigateLeft,
  onNavigateRight,
  currentIndex,
  totalAlbums
}) => {
  const { isMobile } = useWindowSize();
  
  const navigationButtonStyle: React.CSSProperties = {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    color: 'white',
    border: 'none',
    borderRadius: '50%',
    width: isMobile ? '60px' : '50px',
    height: isMobile ? '60px' : '50px',
    fontSize: isMobile ? '2rem' : '1.5rem',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: `all ${theme.transitions.fast}`,
    zIndex: 1000,
    touchAction: 'manipulation', // Improve touch response
  };

  const leftButtonStyle: React.CSSProperties = {
    ...navigationButtonStyle,
    left: theme.spacing.md,
    opacity: canNavigateLeft ? 1 : 0.3,
    cursor: canNavigateLeft ? 'pointer' : 'not-allowed',
  };

  const rightButtonStyle: React.CSSProperties = {
    ...navigationButtonStyle,
    right: theme.spacing.md,
    opacity: canNavigateRight ? 1 : 0.3,
    cursor: canNavigateRight ? 'pointer' : 'not-allowed',
  };

  const counterStyle: React.CSSProperties = {
    position: 'absolute',
    bottom: theme.spacing.md,
    left: '50%',
    transform: 'translateX(-50%)',
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    color: 'white',
    padding: `${theme.spacing.xs} ${theme.spacing.md}`,
    borderRadius: theme.borderRadius.md,
    fontSize: '0.875rem',
    fontWeight: '500',
  };

  const handleLeftClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (canNavigateLeft) {
      onNavigateLeft();
    }
  };

  const handleRightClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (canNavigateRight) {
      onNavigateRight();
    }
  };

  return (
    <>
      <button 
        style={leftButtonStyle}
        onClick={handleLeftClick}
        onMouseEnter={(e) => {
          if (canNavigateLeft) {
            e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.9)';
            e.currentTarget.style.transform = 'translateY(-50%) scale(1.1)';
          }
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.7)';
          e.currentTarget.style.transform = 'translateY(-50%) scale(1)';
        }}
        title="Previous album (←)"
      >
        ‹
      </button>

      <button 
        style={rightButtonStyle}
        onClick={handleRightClick}
        onMouseEnter={(e) => {
          if (canNavigateRight) {
            e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.9)';
            e.currentTarget.style.transform = 'translateY(-50%) scale(1.1)';
          }
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.7)';
          e.currentTarget.style.transform = 'translateY(-50%) scale(1)';
        }}
        title="Next album (→)"
      >
        ›
      </button>

      <div style={counterStyle}>
        {currentIndex + 1} of {totalAlbums}
      </div>
    </>
  );
};

export default AlbumNavigation;